import React, { useState, useEffect } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import DigitalCertificate from "../../components/DigitalCertificate";

// ===== API base - FIXED =====
const API_BASE = 
  (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_API_BASE_URL) ||
  (typeof process !== "undefined" && (process as any)?.env?.REACT_APP_API_BASE_URL) ||
  ""; // ✅ FIXED: Added fallback URL

// ===== Enhanced JSON fetch with debugging =====
async function getJSON(url: string) {
  console.log(`🔄 Making request to: ${url}`);
  const res = await fetch(url, { credentials: "include" });
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  
  if (!res.ok) {
    const sample = (await res.text()).slice(0, 160);
    console.log(`❌ Error response: ${res.status} ${res.statusText}`);
    throw new Error(`${res.status} ${res.statusText} @ ${url} :: ${sample}`);
  }
  
  if (!ct.includes("application/json")) {
    const sample = (await res.text()).slice(0, 160);
    console.log(`⚠️ Non-JSON response: ${ct}`);
    throw new Error(`Expected JSON, got "${ct}" @ ${url}. First bytes: ${sample}`);
  }
  
  const data = await res.json();
  console.log(`✅ Success from ${url}:`, data);
  return data;
}

async function fetchFirstJson(pathCandidates: string[]) {
  console.log("🔍 Trying endpoints:", pathCandidates);
  console.log("🔍 API_BASE:", API_BASE);
  
  for (let i = 0; i < pathCandidates.length; i++) {
    const p = pathCandidates[i];
    const url = p.startsWith("http") ? p : `${API_BASE}${p}`;
    
    try {
      const data = await getJSON(url);
      console.log(`✅ Success on attempt ${i + 1}: ${url}`);
      return { data, okUrl: url };
    } catch (err: any) {
      console.log(`❌ Failed attempt ${i + 1}: ${url} - ${err.message}`);
      continue;
    }
  }
  
  console.log("💀 All endpoints failed");
  return { data: null, okUrl: null as string | null };
}

// ===== API-side models =====
interface ApiBusiness {
  id?: string;
  businessId?: string;
  companyName?: string;
  tradingName?: string;
  registrationNumber?: string;
  industry?: string;
  website?: string;
  phone?: string;
  email?: string;
  address?: {
    street?: string; city?: string; state?: string; postalCode?: string; country?: string;
  };
  contactPerson?: {
    firstName?: string; lastName?: string; email?: string; phone?: string; position?: string;
  };
  verification?: { isVerified?: boolean; verifiedAt?: string };
  establishedYear?: number;
  logoUrl?: string;
  status?: string;
  updatedAt?: string;
}

interface ApiSupplyChain {
  supplyChainId?: string;
  stages?: Array<{
    id?: string | number;
    name?: string;
    location?: string;
    timestamp?: string;
    status?: string;
    vendor?: string;
    details?: string;
  }>;
  currentStage?: string;
  totalStages?: number;
}

interface ApiStore {
  id?: string;
  storeId: string;
  storeName: string;
  address?: { street?: string; city?: string; state?: string; country?: string };
  coordinates?: { latitude?: number; longitude?: number };
  manager?: string;
  phone?: string;
}

interface ApiBatch {
  id?: string;
  batchId: string;
  batchNumber?: string;
  manufacturing?: { date?: string; location?: string; facility?: string };
  expiryDate?: string;
  status?: string;
  isExpired?: boolean;
  qualityChecks?: Array<{ type?: string; result?: string; date?: string }>;
}

interface ApiProduct {
  id?: string;
  productId?: string;
  name?: string;
  brand?: string;
  category?: string;
  sku?: string;
  description?: string;
  specifications?: Record<string, string>;
  warranty?: string;
  image?: string;
}

// ===== Component =====


export default function DigitalCertificatePage(): JSX.Element {
  const location = useLocation() as any;
  const params = useParams<{ tagId?: string }>();
  const navigate = useNavigate(); // <-- MOVE UP HERE!
  const [inputTagId, setInputTagId] = useState(""); // <-- MOVE UP

  const { product: stateProduct, tagId: stateTagId, consumer, scanTime, business } = (location.state || {}) as {
    product?: any;
    tagId?: string;
    consumer?: any;
    scanTime?: string;
    business?: any;
  };

  // Fallback: use param if state missing
  const [product, setProduct] = useState<any>(stateProduct);
  const [tagId, setTagId] = useState<string | undefined>(stateTagId || params.tagId);

  const [businessDetails, setBusinessDetails] = useState<ApiBusiness | null>(null);
  const [supplyChainData, setSupplyChainData] = useState<ApiSupplyChain | null>(null);
  const [storeData, setStoreData] = useState<ApiStore | null>(null);
  const [batchData, setBatchData] = useState<ApiBatch | null>(null);
  const [productDetails, setProductDetails] = useState<ApiProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // -------- Fallbacks --------
  const getDefaultProduct = () => ({
    name: product?.name || "Product Name Not Available",
    brand: product?.brand || "Brand Not Available",
    batch: product?.batch || "N/A",
    warranty: product?.warranty || "Not specified",
    category: product?.category || "General",
    image: product?.image || null
  });

  const getDefaultConsumer = () => ({
    name: consumer?.name || "Anonymous User",
    email: consumer?.email || null,
    id: consumer?.id || null
  });

  // -------- API fetchers --------
  async function fetchBusiness(businessId: string) {
    const { data } = await fetchFirstJson([
      `/api/business/${businessId}/public`,
      `/api/businesses/${businessId}/public`,
      `/api/business/${businessId}`,
      `/api/businesses/${businessId}`
    ]);
    if (!data) return null;
    const src = data.business || data.data || data;
    return {
      id: src?.businessId ?? src?.id,
      businessId: src?.businessId ?? src?.id,
      companyName: src?.companyName ?? src?.name,
      tradingName: src?.tradingName,
      registrationNumber: src?.registrationNumber,
      industry: src?.industry,
      website: src?.website,
      phone: src?.phone,
      email: src?.email,
      address: src?.address,
      contactPerson: src?.contactPerson,
      verification: {
        isVerified: (src?.status ?? "").toLowerCase() === "approved",
        verifiedAt: src?.updatedAt
      },
      establishedYear: src?.establishedYear,
      logoUrl: src?.logoUrl || src?.logo,
      status: src?.status,
      updatedAt: src?.updatedAt
    } as ApiBusiness;
  }

  async function fetchSupplyChain(tagId: string) {
    const { data } = await fetchFirstJson([
      `/api/supply-chain/track/${tagId}`,
      `/api/supplychain/track/${tagId}`,
      `/api/tags/${tagId}/supply-chain`,
      `/api/track/${tagId}`
    ]);
    if (!data) return null;
    return (data.supplyChain || data.data || data) as ApiSupplyChain;
  }

  async function fetchStore(storeId: string) {
    const { data } = await fetchFirstJson([
      `/api/store/${storeId}`,
      `/api/stores/${storeId}`,
      `/api/business/stores/${storeId}`
    ]);
    if (!data) return null;
    return (data.store || data.data || data) as ApiStore;
  }

  async function fetchBatch(batchId: string) {
    const { data } = await fetchFirstJson([
      `/api/batch/${batchId}`,
      `/api/batches/${batchId}`,
      `/api/batches/by-batchId/${batchId}`
    ]);
    if (!data) return null;
    return (data.batch || data.data || data) as ApiBatch;
  }

  async function fetchProduct(productId: string) {
    const { data } = await fetchFirstJson([
      `/api/product/${productId}`,
      `/api/products/${productId}`,
      `/api/business/products/${productId}`
    ]);
    if (!data) return null;
    return (data.product || data.data || data) as ApiProduct;
  }

  useEffect(() => {
    // If product missing but tagId present: Try to fetch product info by tagId
    if (!product && tagId) {
      (async () => {
        try {
          setLoading(true);
          setError(null);

          // Try to get product info by tagId
          const prodData = await fetchFirstJson([
            `/api/tags/${tagId}/product`,
            `/api/scan/${tagId}/product`,
            `/api/products/by-tag/${tagId}`
          ]);
          if (prodData?.data?.product) {
            setProduct(prodData.data.product);
          }
        } catch (e: any) {
          setError(e?.message || "Failed to fetch product by tagId");
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [tagId, product]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const potentialBusinessIds = [
          business?.businessId,
          business?.id,
          product?.businessId,
          product?.business?.businessId,
          product?.business?.id,
          product?.businessInfo?.businessId,
          product?.businessInfo?.id,
          typeof product?.business === 'string' ? product?.business : null,
          product?.business_id,
          product?.businessID,
        ].filter(Boolean);

        let businessId = potentialBusinessIds[0] || "BIZ46842130";

        const productId = product?.productId || product?.id;
        const storeId = product?.storeId;
        const batchId = product?.batchId;

        let biz: ApiBusiness | null = null;
        if (businessId) {
          try {
            biz = await fetchBusiness(businessId);
          } catch (bizError: any) {}
        } else if (business) {
          biz = business;
        }

        const [sc, st, ba, pr] = await Promise.all([
          tagId ? fetchSupplyChain(tagId).catch(e => null) : Promise.resolve(null),
          storeId ? fetchStore(storeId).catch(e => null) : Promise.resolve(null),
          batchId ? fetchBatch(batchId).catch(e => null) : Promise.resolve(null),
          productId ? fetchProduct(productId).catch(e => null) : Promise.resolve(null)
        ]);

        setBusinessDetails(biz);
        setSupplyChainData(sc);
        setStoreData(st);
        setBatchData(ba);
        setProductDetails(pr);
      } catch (e: any) {
        setError(e?.message || "Failed to fetch additional data");
      } finally {
        setLoading(false);
      }
    })();
  }, [tagId, business, product]);

  function mapBusinessDetails(src: ApiBusiness) {
    return {
      id: String(src.businessId || src.id || ""),
      businessId: String(src.businessId || src.id || ""),
      companyName: src.companyName && src.companyName.trim() !== "" ? src.companyName : "Business",
      tradingName: src.tradingName && src.tradingName.trim() !== "" ? src.tradingName : src.companyName,
      registrationNumber: src.registrationNumber,
      industry: src.industry,
      website: src.website,
      phone: src.phone,
      email: src.email,
      address: {
        street: src.address?.street,
        city: src.address?.city,
        state: src.address?.state,
        postalCode: src.address?.postalCode,
        country: src.address?.country,
      },
      contactPerson: {
        firstName: src.contactPerson?.firstName,
        lastName:  src.contactPerson?.lastName,
        email:     src.contactPerson?.email,
        phone:     src.contactPerson?.phone,
        position:  src.contactPerson?.position,
      },
      verification: {
        isVerified: Boolean(
          src.verification?.isVerified ?? (src.status ? src.status.toLowerCase() === "approved" : false)
        ),
        verifiedAt: src.verification?.verifiedAt || src.updatedAt || undefined,
      },
      establishedYear: src.establishedYear,
      logoUrl: src.logoUrl,
    } as ApiBusiness;
  }

  function mapSupplyChain(sc: ApiSupplyChain) {
    const stages = (sc.stages || []).map((s, i) => ({
      id: typeof s.id === "number" ? s.id : i,
      name: s.name || "",
      location: s.location || "",
      timestamp: s.timestamp || new Date().toISOString(),
      status: (s.status || "pending"),
      vendor: s.vendor,
      details: s.details,
    }));
    return {
      supplyChainId: sc.supplyChainId || "",
      stages,
      currentStage: sc.currentStage || "",
      totalStages: typeof sc.totalStages === "number" ? sc.totalStages : stages.length,
    } as any;
  }

  function mapStore(store: ApiStore) {
    return {
      id: String(store.id || store.storeId || ""),
      storeId: String(store.storeId),
      storeName: store.storeName || "",
      address: {
        street: store.address?.street || "",
        city: store.address?.city || "",
        state: store.address?.state || "",
        country: store.address?.country || "",
      },
      coordinates:
        store.coordinates?.latitude != null && store.coordinates?.longitude != null
          ? { latitude: Number(store.coordinates.latitude), longitude: Number(store.coordinates.longitude) }
          : undefined,
      manager: store.manager,
      phone: store.phone,
    } as any;
  }

  function mapBatch(batch: ApiBatch) {
    return {
      id: String(batch.id || batch.batchId || ""),
      batchId: String(batch.batchId),
      batchNumber: batch.batchNumber || "",
      manufacturing: {
        date: batch.manufacturing?.date,
        location: batch.manufacturing?.location,
        facility: batch.manufacturing?.facility,
      },
      expiryDate: batch.expiryDate,
      status: batch.status,
      isExpired: batch.isExpired,
      qualityChecks: (batch.qualityChecks || []).map((q) => ({
        type: q.type,
        result: q.result,
        date: q.date,
      })),
    } as any;
  }

  function mapProduct(p: ApiProduct) {
    return {
      id: String(p.id || p.productId || ""),
      productId: String(p.productId || p.id || ""),
      name: p.name,
      brand: p.brand,
      category: p.category,
      sku: p.sku,
      description: p.description,
      specifications: p.specifications,
      warranty: p.warranty,
      image: p.image,
    } as any;
  }

  const enhancedProduct = {
    ...getDefaultProduct(),
    ...(productDetails ? mapProduct(productDetails) : {}),
    batch: (batchData?.batchNumber ?? (product as any)?.batch) || "N/A",
    warranty: productDetails?.warranty || product?.warranty || "Not specified",
    image: productDetails?.image || product?.image || null
  };

  const enhancedConsumer = { ...getDefaultConsumer() };

  // -------- UX FIX: Show input if no product and no tagId --------
  
  // Then use your conditional rendering below:
  if (!loading && !product && !tagId) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center mb-6">
            <h2 className="text-xl font-bold text-red-800 mb-2">No Certificate Data Available</h2>
            <p className="text-red-600 mb-4">
              No product or tag information was provided. Please enter a Tag ID to view a certificate.
            </p>
            <input
              type="text"
              className="border px-3 py-2 rounded w-64"
              placeholder="Enter Tag ID"
              value={inputTagId}
              onChange={e => setInputTagId(e.target.value)}
            />
            <button
              className="ml-2 px-4 py-2 bg-blue-600 text-white rounded"
              onClick={() => inputTagId && navigate(`/consumer/certificate/${inputTagId}`)}
            >
              View Certificate
            </button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading certificate details...</p>
          </div>
        )}

        {error && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="text-yellow-600 mr-2">⚠️</div>
              <p className="text-yellow-800">{error}</p>
              <p className="text-sm text-yellow-600 ml-2">(Displaying available data)</p>
            </div>
          </div>
        )}

        <DigitalCertificate
          product={enhancedProduct}
          tagId={tagId || "Tag ID Not Available"}
          consumer={enhancedConsumer}
          scanTime={scanTime || new Date().toLocaleString()}
          businessDetails={businessDetails ? mapBusinessDetails(businessDetails) : undefined}
          supplyChainData={supplyChainData ? mapSupplyChain(supplyChainData) : undefined}
          storeData={storeData ? mapStore(storeData) : undefined}
          batchData={batchData ? mapBatch(batchData) : undefined}
          productDetails={productDetails ? mapProduct(productDetails) : undefined}
        />
      </div>
    </div>
  );
}